import { useMemo, useState, type DragEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { logApplicationFailure, userErrorMessage } from "@/lib/user-error";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { useNavigate, useSearchParams } from "react-router";
import { LoadMore } from "@/components/common/LoadMore";
import { ShareIcon, ShareItemCard } from "@/components/share/ShareItemCard";
import { applySearchSort, type SortDir } from "@/lib/collection-filter";
import { usePagedQuery } from "@/lib/use-paged-query";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { usePin } from "@/pages/save/use-pin";
import {
  PinGroupSidebar,
  type GroupSelection,
  type PinGroupLite,
} from "@/components/save/PinGroupSidebar";
import {
  SaveCollectionControls,
  SaveCollectionEmptyState,
  SaveCollectionGrid,
  SaveCollectionPage,
  SaveGroupPopover,
  SavePersonCollectionCard,
  SaveRemoveItemDialog,
} from "@/components/save/SaveCollectionTemplate";
import {
  MyPinGroupsDocument,
  NumberOfPinnedItemsDocument,
  PinGroupMembershipsDocument,
  PinnedPersonsDocument,
  PinType,
  SharedItemsDocument,
  SharedItemType,
  type PersonFilterInput,
  type PinnedPersonsQuery,
  type SharedItemFilterInput,
  type SharedItemsQuery,
} from "@/graphql/generated/operations";

// Save · Pins. Mirrors discovery's pinboard: left navigation switches pin type
// (Personen / Beiträge / Projekte) and group; the grid shows the pinned items
// with per-card unpin + move-to-group actions. POST and PROJECT are both
// SharedItems (distinguished by itemType), so there are two data sources.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function fullName(name: any): string {
  if (!name) return "–";
  const given = (name.given ?? []).filter(Boolean).join(" ");
  return [given, name.family].filter(Boolean).join(" ") || "–";
}

// Beruf · Stadt · Land (mirrors Community's subtitleOf); "–" when all empty.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function personSubtitle(p: any): string {
  return [p.profession, p.city, p.country?.name].filter(Boolean).join(" · ") || "–";
}

// genesis' filter specs apply only ONE top-level field (early-return in
// *FilterSpec), so several conditions must be combined via the `and` array
// (INNER JOINed). The group clause (pinGroupId / isInAnyPinGroup) is one more
// such condition appended here.
function groupClause(
  group: GroupSelection,
  onlyUngrouped: boolean,
): (PersonFilterInput & SharedItemFilterInput) | null {
  if (group !== "all") return { pinGroupId: { eq: group } };
  if (onlyUngrouped) return { isInAnyPinGroup: { eq: false } };
  return null;
}

export function PinboardPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const client = useApolloClient();
  // Deep link from the home Pins tile: ?type=person|project|post preselects the
  // tab. Read once on mount (the tabs take over from there).
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get("type");
  const [activeType, setActiveType] = useState<PinType>(
    typeParam === "project" ? PinType.Project : typeParam === "post" ? PinType.Post : PinType.Person,
  );
  const [activeGroup, setActiveGroup] = useState<GroupSelection>("all");
  // "Nur ohne Gruppe" toggle, only meaningful on the Alle view (mirrors Community).
  const [onlyUngrouped, setOnlyUngrouped] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortDir>("az");

  const isPerson = activeType === PinType.Person;

  const personWhere: PersonFilterInput = useMemo(() => {
    const clause = groupClause(activeGroup, onlyUngrouped);
    const personBase: PersonFilterInput = { isPinned: { eq: true } };
    return clause ? { and: [personBase, clause] } : personBase;
  }, [activeGroup, onlyUngrouped]);

  const sharedWhere: SharedItemFilterInput = useMemo(() => {
    const clause = groupClause(activeGroup, onlyUngrouped);
    const sharedBase: SharedItemFilterInput[] = [
      { pinned: { eq: true } },
      {
        itemType: {
          eq: activeType === PinType.Project ? SharedItemType.Project : SharedItemType.Post,
        },
      },
    ];
    return { and: clause ? [...sharedBase, clause] : sharedBase };
  }, [activeGroup, onlyUngrouped, activeType]);

  const groupsQ = useQuery(MyPinGroupsDocument, {
    variables: { input: { pinTypes: [activeType] } },
  });
  const countsQ = useQuery(NumberOfPinnedItemsDocument);
  const personInputMemo = useMemo(() => ({ where: personWhere }), [personWhere]);
  const personsPaged = usePagedQuery<PinnedPersonsQuery["persons"]["nodes"][number]>(
    PinnedPersonsDocument,
    { connectionKey: "persons", input: personInputMemo, pageSize: 30, skip: !isPerson },
  );
  const sharedInputMemo = useMemo(() => ({ where: sharedWhere }), [sharedWhere]);
  const sharedPaged = usePagedQuery<SharedItemsQuery["sharedItems"]["nodes"][number]>(
    SharedItemsDocument,
    { connectionKey: "sharedItems", input: sharedInputMemo, pageSize: 30, skip: isPerson },
  );

  const pin = usePin();

  // Drag a card onto a group row in the sidebar to move it there.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  // Pin pending unpin: opens a choice dialog (only from a group view also offers
  // "remove from this group" vs "unpin entirely"). Mirrors Community.
  const [removingPin, setRemovingPin] = useState<{ id: string; title: string } | null>(null);

  const groups: PinGroupLite[] =
    groupsQ.data?.pinGroups.__typename === "PinGroupResult"
      ? groupsQ.data.pinGroups.pinGroups.map((g) => ({
          id: g.id,
          name: g.name,
          itemCount: g.itemCount ?? 0,
        }))
      : [];

  const countsData = countsQ.data?.numberOfPinnedItems;
  const counts =
    countsData?.__typename === "NumberOfPinsResult"
      ? {
          person: countsData.pinnedPersons ?? 0,
          post: countsData.pinnedPosts ?? 0,
          project: countsData.pinnedProjects ?? 0,
        }
      : { person: 0, post: 0, project: 0 };

  const persons = applySearchSort(
    personsPaged.nodes,
    (p) => fullName(p.name),
    search,
    sort,
  );
  const sharedItems = applySearchSort(
    sharedPaged.nodes,
    (it) => it.name ?? "",
    search,
    sort,
  );
  const activeQ = isPerson ? personsPaged : sharedPaged;
  const itemCount = isPerson ? persons.length : sharedItems.length;

  // Page-local refetches. usePin already handles PinGroupMemberships when
  // group ops fire, so we don't repeat it here.
  async function refetchAll() {
    await Promise.all([
      groupsQ.refetch(),
      countsQ.refetch(),
      isPerson ? personsPaged.refetch() : sharedPaged.refetch(),
    ]);
  }

  const pinInput = (id: string) =>
    isPerson ? { personIds: [id] } : { sharedItemIds: [id] };

  async function unpin(id: string) {
    try {
      await pin.remove(pinInput(id));
      refetchAll();
    } catch {
      logApplicationFailure("save.remove-pins");
    }
  }

  // Additive: a pin can sit in several groups at once (like discovery / Community).
  async function addToGroup(id: string, groupId: string) {
    if (!groupId) return;
    try {
      await pin.add({
        ...pinInput(id),
        groups: [groupId],
        removePinsFromOtherGroups: false,
      });
      await refetchAll();
    } catch {
      logApplicationFailure("save.add-pins-to-group");
    }
  }

  // Remove from ONE group while keeping the pin + other memberships: read the
  // current groups, drop the target, re-set the remaining (removePinsFromOther-
  // Groups deletes exactly the dropped one). Same trick as Community.
  async function removeFromGroup(id: string, groupId: string) {
    try {
      const res = await client.query({
        query: PinGroupMembershipsDocument,
        variables: { input: isPerson ? { personId: id } : { sharedItemId: id } },
        fetchPolicy: "network-only",
      });
      const out = res.data?.pinGroupMemberships;
      const current =
        out?.__typename === "PinGroupMembershipResult" ? out.pinGroups.map((g) => g.id) : [];
      const remaining = current.filter((gid) => gid !== groupId);
      await pin.add({
        ...pinInput(id),
        groups: remaining,
        removePinsFromOtherGroups: true,
      });
      await refetchAll();
    } catch {
      logApplicationFailure("save.remove-pins-from-group");
    }
  }

  const dropHandlers = (groupId: string) => ({
    onDragOver: (e: DragEvent) => {
      if (!draggingId) return;
      // Dropping on "Alle" only does something from inside a group (it removes
      // that membership); no source group → no drop affordance.
      if (groupId === "all" && activeGroup === "all") return;
      e.preventDefault();
      if (dropTarget !== groupId) setDropTarget(groupId);
    },
    onDragLeave: () => setDropTarget((t) => (t === groupId ? null : t)),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      if (draggingId) {
        if (groupId === "all") {
          // Drag from a group to "Alle" → remove it from the active group.
          if (activeGroup !== "all") void removeFromGroup(draggingId, activeGroup);
        } else {
          void addToGroup(draggingId, groupId);
        }
      }
      setDropTarget(null);
      setDraggingId(null);
    },
  });

  function dragHandlers(id: string) {
    return {
      draggable: true,
      onDragStart: (e: DragEvent) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", id);
        setDraggingId(id);
      },
      onDragEnd: () => {
        setDraggingId(null);
        setDropTarget(null);
      },
    };
  }

  const typeLabel = isPerson
    ? intl.formatMessage({ id: "save.pinboardPage.typePersons", defaultMessage: "People" })
    : activeType === PinType.Post
      ? intl.formatMessage({ id: "save.pinboardPage.typePosts", defaultMessage: "Posts" })
      : intl.formatMessage({ id: "save.pinboardPage.typeProjects", defaultMessage: "Projects" });
  // When a single group is open, the unpin dialog also offers "remove from this group".
  const activeGroupObj = activeGroup !== "all" ? (groups.find((g) => g.id === activeGroup) ?? null) : null;
  // Paging state for the visible list, driving the <LoadMore /> below the grid.
  const active = isPerson ? personsPaged : sharedPaged;

  // Guided tour (/tour): summary + sidebar (type tabs + groups), grid and controls, with live counts.
  useAssistantTour({
    label: intl.formatMessage({
      id: "pinboard.tour.label",
      defaultMessage: "Save Pins",
    }),
    summary: intl.formatMessage(
      {
        id: "pinboard.tour.summary",
        defaultMessage:
          "This is your pinboard with everything you saved: {people} people, {posts} posts and {projects} projects.",
      },
      { people: counts.person, posts: counts.post, projects: counts.project },
    ),
    stops: [
      {
        anchor: "save.pins.sidebar",
        title: intl.formatMessage({ id: "pinboard.tour.sidebar.title", defaultMessage: "Types & groups" }),
        description: intl.formatMessage(
          {
            id: "pinboard.tour.sidebar.desc",
            defaultMessage:
              "Switch between your pinned people ({people}), posts ({posts}) and projects ({projects}), and pick a pin group below. Drag a card onto a group to file it there.",
          },
          { people: counts.person, posts: counts.post, projects: counts.project },
        ),
      },
      {
        anchor: "save.pins.grid",
        title: intl.formatMessage({ id: "pinboard.tour.grid.title", defaultMessage: "Your pinned items" }),
        description: intl.formatMessage(
          {
            id: "pinboard.tour.grid.desc",
            defaultMessage:
              "{count} {typeLabel} in the current view. Click a card to open it, use \"+ Add\" to put it into groups, or the trash icon to unpin it.",
          },
          { count: itemCount, typeLabel },
        ),
      },
      {
        anchor: "save.pins.controls",
        title: intl.formatMessage({ id: "pinboard.tour.controls.title", defaultMessage: "Search & sort" }),
        description: intl.formatMessage({
          id: "pinboard.tour.controls.desc",
          defaultMessage:
            "Search pins by name, change the sort order, or filter to pins that are not in any group yet.",
        }),
      },
    ],
  });

  // Live assistant context (motive 1, ADR 0020): pinned totals + which tab/group is open (counts +
  // view-state, not the pinned records).
  useAssistantContext([
    {
      description: "the user's pinboard",
      value:
        `pinned totals — ${counts.person} people, ${counts.post} posts, ${counts.project} projects; ` +
        `viewing ${typeLabel}` +
        (activeGroupObj ? ` in group "${activeGroupObj.name}"` : "") +
        (onlyUngrouped ? " (ungrouped only)" : ""),
    },
  ]);

  return (
    <>
      <SaveCollectionPage
        title={intl.formatMessage({ id: "save.pinboardPage.title", defaultMessage: "Pins" })}
        dataGraphql="PinnedPersons"
        showBackButton
        description={
          <FormattedMessage
            id="save.pinboardPage.description"
            defaultMessage="Drag a card onto a group on the left, or add it to one or more groups via “Add”. Dragging onto “All” removes it from the current group."
          />
        }
        sidebar={
          <div data-assistant-anchor="save.pins.sidebar">
          <PinGroupSidebar
            activeType={activeType}
            onTypeChange={(t) => {
              setActiveType(t);
              setActiveGroup("all");
              setOnlyUngrouped(false);
            }}
            counts={counts}
            groups={groups}
            activeGroup={activeGroup}
            onGroupChange={setActiveGroup}
            onGroupsChanged={refetchAll}
            dropTargetId={dropTarget}
            dropHandlers={dropHandlers}
          />
          </div>
        }
      >
        <div data-assistant-anchor="save.pins.controls">
        <SaveCollectionControls
          statusLabel={`${itemCount} ${typeLabel}`}
          loading={activeQ.loading}
          errorMessage={activeQ.error ? userErrorMessage(intl, activeQ.error) : undefined}
          search={search}
          sort={sort}
          showUngroupedToggle={activeGroup === "all"}
          ungroupedLabel={intl.formatMessage({
            id: "save.pinboardPage.ungroupedLabel",
            defaultMessage: "Pins without a group",
          })}
          ungroupedActive={onlyUngrouped}
          onSearchChange={setSearch}
          onSortChange={setSort}
          onUngroupedToggle={() => setOnlyUngrouped((v) => !v)}
        />
        </div>

        <div data-assistant-anchor="save.pins.grid">
        {!activeQ.loading && !activeQ.error && itemCount === 0 ? (
          <SaveCollectionEmptyState>
            {activeGroup === "all" && onlyUngrouped ? (
              <FormattedMessage
                id="save.pinboardPage.emptyAllGrouped"
                defaultMessage="All pinned {typeLabel} are in groups."
                values={{ typeLabel }}
              />
            ) : (
              <FormattedMessage
                id="save.pinboardPage.emptyNoneInView"
                defaultMessage="No pinned {typeLabel} in this view."
                values={{ typeLabel }}
              />
            )}
          </SaveCollectionEmptyState>
        ) : (
          <SaveCollectionGrid variant={isPerson ? "person" : "shared"}>
            {isPerson
              ? persons.map((p) => (
                  <PinCard
                    key={p.id}
                    itemId={p.id}
                    isPerson
                    title={fullName(p.name)}
                    sub={personSubtitle(p)}
                    avatarContent={p.avatar?.content}
                    avatarMime={p.avatar?.mimeType}
                    groups={groups}
                    drag={dragHandlers(p.id)}
                    onUnpin={() => setRemovingPin({ id: p.id, title: fullName(p.name) })}
                    onAdd={(gid) => addToGroup(p.id, gid)}
                    onRemoveGroup={(gid) => removeFromGroup(p.id, gid)}
                  />
                ))
              : sharedItems.map((item, i) => (
                  <div
                    key={item.id}
                    {...dragHandlers(item.id)}
                    onClick={() => navigate(`/share/discover/${item.id}`)}
                    role="button"
                    title={intl.formatMessage(
                      { id: "save.pinboardPage.openItem", defaultMessage: "Open {name}" },
                      {
                        name:
                          item.name ??
                          intl.formatMessage({
                            id: "save.pinboardPage.itemFallback",
                            defaultMessage: "Entry",
                          }),
                      },
                    )}
                    className="relative cursor-pointer active:cursor-grabbing"
                  >
                    <ShareItemCard item={item} index={i} />
                    <div
                      className="absolute right-2 top-2 z-10 flex items-center gap-1.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <AddToPinGroup
                        itemId={item.id}
                        isPerson={false}
                        options={groups}
                        onAdd={(gid) => addToGroup(item.id, gid)}
                        onRemoveGroup={(gid) => removeFromGroup(item.id, gid)}
                        glass
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setRemovingPin({
                            id: item.id,
                            title:
                              item.name ??
                              intl.formatMessage({
                                id: "save.pinboardPage.itemFallback",
                                defaultMessage: "Entry",
                              }),
                          })
                        }
                        title={intl.formatMessage({
                          id: "save.pinboardPage.unpin",
                          defaultMessage: "Unpin",
                        })}
                        aria-label={intl.formatMessage({
                          id: "save.pinboardPage.removePin",
                          defaultMessage: "Remove pin",
                        })}
                        className="rounded-full bg-white/30 p-1.5 text-ink/45 shadow-sm ring-1 ring-white/30 backdrop-blur-md transition hover:bg-surface hover:text-accent"
                      >
                        <ShareIcon name="trash" className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
          </SaveCollectionGrid>
        )}
        </div>

        <LoadMore
          hasMore={active.hasMore}
          loading={active.loadingMore}
          paused={active.loadMoreFailed}
          onLoadMore={active.loadMore}
        />
      </SaveCollectionPage>

      {removingPin ? (
        <SaveRemoveItemDialog
          open
          title={intl.formatMessage({
            id: "save.pinboardPage.removePin",
            defaultMessage: "Remove pin",
          })}
          onClose={() => setRemovingPin(null)}
          message={
            activeGroupObj ? (
              <FormattedMessage
                id="save.pinboardPage.removeFromGroupMessage"
                defaultMessage="Remove “{title}” from “{group}” (stays pinned and in other groups) or unpin completely?"
                values={{ title: removingPin.title, group: activeGroupObj.name }}
              />
            ) : (
              <FormattedMessage
                id="save.pinboardPage.unpinMessage"
                defaultMessage="Unpin “{title}”?"
                values={{ title: removingPin.title }}
              />
            )
          }
          secondaryActionLabel={
            activeGroupObj
              ? intl.formatMessage({
                  id: "save.pinboardPage.removeFromGroup",
                  defaultMessage: "Remove from group",
                })
              : undefined
          }
          onSecondaryAction={
            activeGroupObj
              ? () => {
                  const p = removingPin;
                  setRemovingPin(null);
                  if (p) void removeFromGroup(p.id, activeGroupObj.id);
                }
              : undefined
          }
          destructiveActionLabel={intl.formatMessage({
            id: "save.pinboardPage.unpinCompletely",
            defaultMessage: "Unpin completely",
          })}
          onDestructiveAction={() => {
            const p = removingPin;
            setRemovingPin(null);
            if (p) void unpin(p.id);
          }}
        />
      ) : null}
    </>
  );
}

// Group-assign pill. A pin lives in at most ONE group (single-group, unlike
// Community contacts), so the popover is a move/assign: the current group is
// marked with a check (click it to ungroup), clicking another moves the pin
// there. The trigger label reflects state: "Verschieben" when the pin is
// already in a group, "+ Hinzufügen" when it is in none. Membership is loaded
// eagerly because the closed label depends on it.
function AddToPinGroup({
  itemId,
  isPerson,
  options,
  onAdd,
  onRemoveGroup,
  glass = false,
}: {
  itemId: string;
  isPerson: boolean;
  options: PinGroupLite[];
  onAdd: (groupId: string) => Promise<void> | void;
  onRemoveGroup: (groupId: string) => Promise<void> | void;
  // Glass treatment for the floating overlay over cover images (Posts/Projekte);
  // default is the quiet grey chip used on the light person card (matches Community).
  glass?: boolean;
}) {
  const intl = useIntl();
  // Group id whose add/remove is in flight, for a per-pill spinner.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const { data } = useQuery(PinGroupMembershipsDocument, {
    variables: { input: isPerson ? { personId: itemId } : { sharedItemId: itemId } },
    fetchPolicy: "cache-and-network",
  });
  const memberOut = data?.pinGroupMemberships;
  const memberIds = new Set<string>(
    memberOut?.__typename === "PinGroupMembershipResult"
      ? memberOut.pinGroups.map((g) => g.id)
      : [],
  );

  async function toggle(groupId: string, isMember: boolean) {
    if (pendingId) return;
    setPendingId(groupId);
    try {
      await (isMember ? onRemoveGroup(groupId) : onAdd(groupId));
    } finally {
      setPendingId(null);
    }
  }

  if (options.length === 0) return null;
  return (
    <SaveGroupPopover
      triggerLabel={`+ ${intl.formatMessage({ id: "common.add", defaultMessage: "Add" })}`}
      options={options}
      memberIds={memberIds}
      pendingId={pendingId}
      glass={glass}
      onToggle={(groupId, isMember) => void toggle(groupId, isMember)}
    />
  );
}

// Person pin card. Mirrors Community's ContactCard: the whole card is clickable
// (opens the profile), draggable onto a sidebar group, and hosts its actions
// inside the row (Verschieben popover + trash). stopPropagation keeps action
// clicks from triggering the card's navigation.
function PinCard({
  itemId,
  isPerson,
  title,
  sub,
  avatarContent,
  avatarMime,
  groups,
  drag,
  onUnpin,
  onAdd,
  onRemoveGroup,
}: {
  itemId: string;
  isPerson: boolean;
  title: string;
  sub: string;
  avatarContent?: string | null;
  avatarMime?: string | null;
  groups: PinGroupLite[];
  drag: { draggable: boolean; onDragStart: (e: DragEvent) => void; onDragEnd: () => void };
  onUnpin: () => void;
  onAdd: (groupId: string) => Promise<void> | void;
  onRemoveGroup: (groupId: string) => Promise<void> | void;
}) {
  const intl = useIntl();
  return (
    <SavePersonCollectionCard
      id={itemId}
      title={title}
      subtitle={sub}
      avatarContent={avatarContent}
      avatarMime={avatarMime}
      draggable={drag.draggable}
      onDragStart={drag.onDragStart}
      onDragEnd={drag.onDragEnd}
      openHref={isPerson ? `/p/${itemId}` : undefined}
      onRemove={onUnpin}
      removeLabel={intl.formatMessage({
        id: "save.pinboardPage.removePin",
        defaultMessage: "Remove pin",
      })}
      actions={
        <AddToPinGroup
          itemId={itemId}
          isPerson={isPerson}
          options={groups}
          onAdd={onAdd}
          onRemoveGroup={onRemoveGroup}
        />
      }
    />
  );
}
